/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Coupon } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { ICouponMinimumSpendEntity } from '../actions/DataServiceEntities.g';

/**
 * Gets the largest minimum spend among the applied coupons that the cart has not met.
 * Where more than one coupon is short, the largest is the one to report: naming a smaller amount would give
 * the customer a target that still leaves checkout blocked once they reach it.
 * @param coupons - The coupons applied to the cart.
 * @param couponMinimumSpends - The minimum spend for each coupon code that has one.
 * @param cartTotal - The cart amount to compare against.
 * @returns The unmet minimum spend, or undefined when every applied coupon qualifies.
 */
export const getUnmetCouponMinimumSpend = (
    coupons: Coupon[] | undefined,
    couponMinimumSpends: ICouponMinimumSpendEntity[],
    cartTotal: number
): ICouponMinimumSpendEntity | undefined => {
    if (!coupons || coupons.length === 0) {
        return undefined;
    }

    return couponMinimumSpends
        .filter(
            minimumSpend =>
                minimumSpend.MinimumSpend > cartTotal &&
                coupons.some(coupon => coupon.Code?.toUpperCase() === minimumSpend.CouponCode.toUpperCase())
        )
        .reduce<ICouponMinimumSpendEntity | undefined>(
            (largest, current) => (largest && largest.MinimumSpend >= current.MinimumSpend ? largest : current),
            undefined
        );
};

/**
 * Clones a checkout button so that clicking it raises the minimum spend message instead of proceeding.
 * The cart page renders a checkout button in the order summary and a second one above the cart lines for
 * mobile, so both have to be wrapped or the block is bypassable on a phone.
 * @param checkoutButton - The checkout button to wrap.
 * @param onBlocked - Called instead of the button's own click handler.
 * @returns The wrapped button, or the original node when it is not an element.
 */
export const blockCheckoutButton = (checkoutButton: React.ReactNode, onBlocked: () => void): React.ReactNode => {
    if (!React.isValidElement(checkoutButton)) {
        return checkoutButton;
    }

    return React.cloneElement(checkoutButton as React.ReactElement, {
        onClick: (event: React.MouseEvent) => {
            event.preventDefault?.();
            event.stopPropagation?.();
            onBlocked();
        },
        href: undefined,
        to: undefined
    });
};
